import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Layout,
  Row,
  Col,
  Card,
  Input,
  Select,
  Button,
  Table,
  Tag,
  Typography,
  Tooltip,
} from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import "./AssetManagement.css";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { useDispatch, useSelector } from "react-redux";
import { actions, reducer, sliceKey } from "./slice";
import { assetManagementSaga } from "./saga";
import * as selectors from "./selectors";
import CreateKit from "./components/createKit";
import AssignSampler from "./components/AssignSampler";
import SuccessModal from "./components/SuccessModal";
import { useAuth } from "../../../../contexts/auth";
import {
  getAgencyFilters,
  getDistrictFilters,
  getProjectFilters,
  getSamplerFilters,
  getStateFilters,
  getStatusFilters,
} from "./helpers";
import KitAction from "./components/KitAction";

const { Title, Text } = Typography;

const statusColors = {
  Packed: "blue",
  Pending: "gold",
  "Out for delivery": "purple",
  Delivered: "green",
};

const STORAGE_KEY = 'assetManagementFilters';

const AssetManagement = () => {
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: assetManagementSaga });
  const dispatch = useDispatch();
  const auth = useAuth();
  const user = auth.loggedInUser;
  const userRole = user.role;
  const history = useHistory();
  const location = useLocation();
  const kitLists = useSelector(selectors.selectKitLists);
  const kitListsCount = useSelector(selectors.selectKitListsCount);
  const states = useSelector(selectors.selectStates);
  const districts = useSelector(selectors.selectFilterDistricts);
  const agencies = useSelector(selectors.selectAgencies);
  const filterStatus = useSelector(selectors.selectFilterStatus);
  const projects = useSelector(selectors.selectProjects);
  const samplers = useSelector(selectors.selectFilterSamplers);
  const kitDataAfterKitCreation = useSelector(
    selectors.selectKitDataAfterKitCreation
  );

  // Initialize state from sessionStorage or defaults
  const getInitialState = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to parse saved filters:', e);
    }
    
    return {
      filters: {
        searchQuery: "",
        status: "",
        sampler: "",
        state: "",
        district: "",
        agency: "",
        project: "",
      },
      page: 1,
      pageLimit: 10,
    };
  };

  const initialState = getInitialState();
  const [filters, setFilters] = useState(initialState.filters);
  const [page, setPage] = useState(initialState.page);
  const [pageLimit, setPageLimit] = useState(initialState.pageLimit);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateKitModalVisible, setIsCreateKitModalVisible] = useState(false);
  const [isAssignSamplerModalVisible, setIsAssignSamplerModalVisible] = useState(false);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    const state = { filters, page, pageLimit };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save filters:', e);
    }
  }, [filters, page, pageLimit]);

  // Table columns
  const columns = [
    {
      title: "Kit ID",
      dataIndex: "kitId",
      key: "kitId",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Sampler Name",
      dataIndex: "samplerName",
      key: "samplerName",
      ellipsis: true,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      ellipsis: true,
    },
    {
      title: "District",
      dataIndex: "district",
      key: "district",
      ellipsis: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      ellipsis: true,
      render: (status) => (
        <Tag
          color={statusColors[status]}
          style={{
            borderRadius: 8,
            fontWeight: 800,
            border: "none",
            padding: "4px 15px",
          }}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: "Agency",
      dataIndex: "agency",
      key: "agency",
      ellipsis: true,
    },
    {
      title: "Project ID",
      dataIndex: "projectId",
      key: "projectId",
      ellipsis: true,
    },
    {
      title: "Logistic",
      dataIndex: "logistic",
      key: "logistic",
      ellipsis: true,
      width: 120,
    },
    {
      title: "Tracking ID",
      dataIndex: "trackingId",
      key: "trackingId",
      ellipsis: true,
      width: 120,
    },
    ...(userRole === "support_team"
      ? []
      : [
          {
            title: "Actions",
            key: "actions",
            width: 100,
            fixed: "right",
            render: (_, record) => (
              <KitAction
                record={record?.kitData}
                fetchKitLists={fetchKitLists}
                userRole={userRole}
              />
            ),
          },
        ]),
  ];

  const dataSource = kitLists?.map((kit) => {
    return {
      key: kit?._id,
      kitId: kit?.kitNumber,
      samplerName: kit?.sampler?.name || "Not Assigned",
      state: kit?.state?.name || "-",
      district: kit?.district?.name || "-",
      agency: kit?.agency?.name || "-",
      projectId: kit?.projectCode?.projectcode || "-",
      status: kit?.status?.label || "-",
      logistic: kit?.assignedLogistic?.name || "-",
      trackingId: kit?.trackingId || "-",
      kitData: kit,
    };
  });

  const handlePaginationChange = (pagination, tableFilters, sorter) => {
    setPage(pagination.current);
    setPageLimit(pagination.pageSize);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1); // Reset to page 1 on filter change
  };

  const fetchKitLists = useCallback(() => {
    setIsLoading(true);
    const data = {
      searchQuery: filters?.searchQuery,
      statusId: filters?.status,
      samplerId: filters?.sampler,
      stateId: filters?.state,
      districtId: filters?.district,
      agencyId: filters?.agency,
      projectCodeId: filters?.project,
      page: page,
      limit: pageLimit,
    };
    dispatch(
      actions.fetchKitLists({
        data,
        onSuccess: () => setIsLoading(false),
        onFailure: () => setIsLoading(false),
      })
    );
  }, [dispatch, filters, page, pageLimit]);

  // Fetch dropdown options on mount
  useEffect(() => {
    dispatch(actions.fetchStates());
    dispatch(actions.fetchAgency());
    dispatch(actions.fetchStatus());
    dispatch(actions.fetchProjects());
    dispatch(actions.getLogisticList());
    dispatch(actions.getSampleSealTypes());
  }, [dispatch]);

  // Fetch districts when state changes
  useEffect(() => {
    if (filters?.state) {
      dispatch(
        actions.fetchDistricts({ stateId: filters.state, For: "filter" })
      );
    }
    if (!filters?.state && filters?.district) {
      setFilters((prev) => ({ ...prev, district: "" }));
    }
  }, [dispatch, filters?.state, filters?.district]);

  // Fetch samplers when state and district change
  useEffect(() => {
    if (filters?.state && filters?.district) {
      dispatch(
        actions.fetchSamplers({
          stateId: filters.state,
          districtId: filters.district,
          For: "filter",
        })
      );
    }
    if (!filters?.district && filters?.sampler) {
      setFilters((prev) => ({ ...prev, sampler: "" }));
    }
  }, [dispatch, filters?.district, filters?.state, filters?.sampler]);

  // Fetch kit lists when filters change
  useEffect(() => {
    fetchKitLists();
  }, [fetchKitLists]);

  return (
    <Layout
      style={{ background: "#f6f7fb", padding: 0, margin: 0, overflow: "auto" }}
    >
      <div
        style={{ padding: "24px 0 0 0", maxWidth: "77vw", margin: "0 auto" }}
      >
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Title
              level={2}
              style={{
                margin: 0,
                color: "#23235F",
                fontWeight: 900,
                fontSize: 36,
              }}
            >
              Assets
            </Title>
          </Col>
          {userRole !== "agnext_reviewer" && userRole !== "support_team" && (
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                size="large"
                style={{ background: "#23235F", borderRadius: 8 }}
                onClick={() => setIsCreateKitModalVisible(true)}
              >
                Create New Kit
              </Button>
            </Col>
          )}
        </Row>
        <Card
          style={{
            marginBottom: 24,
            borderRadius: 16,
            background: "#fff",
            boxShadow:
              "0 4px 24px 0 rgba(44, 39, 56, 0.08), 0 1.5px 4px 0 rgba(44, 39, 56, 0.12)",
            border: "none",
          }}
          bodyStyle={{ padding: 24 }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Input
                placeholder="Search by Kit ID or Seal ID..."
                allowClear
                className="filter-input"
                prefix={
                  <SearchOutlined
                    style={{ color: "grey", padding: "2px", fontSize: "16px" }}
                  />
                }
                value={filters?.searchQuery}
                onChange={(e) =>
                  handleFilterChange("searchQuery", e.target.value)
                }
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="All Status"
                className="filter-select"
                style={{ width: "100%" }}
                options={getStatusFilters(filterStatus)}
                value={filters?.status || "All Status"}
                onChange={(value) => handleFilterChange("status", value)}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="All Agencies"
                className="filter-select"
                style={{ width: "100%" }}
                options={getAgencyFilters(agencies)}
                value={filters?.agency || "All Agencies"}
                onChange={(value) => handleFilterChange("agency", value)}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="All Projects"
                className="filter-select"
                style={{ width: "100%" }}
                options={getProjectFilters(projects)}
                value={filters?.project || "All Projects"}
                onChange={(value) => handleFilterChange("project", value)}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="All States"
                className="filter-select"
                style={{ width: "100%" }}
                options={getStateFilters(states)}
                value={filters?.state || "All States"}
                onChange={(value) => handleFilterChange("state", value)}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Tooltip
                title={!filters?.state ? "Please select a state first" : ""}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  defaultValue="All Districts"
                  placeholder="All Districts"
                  className="filter-select"
                  style={{ width: "100%" }}
                  disabled={!filters?.state}
                  options={getDistrictFilters(districts)}
                  value={filters?.district || "All Districts"}
                  onChange={(value) => handleFilterChange("district", value)}
                />
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Tooltip
                title={
                  !filters?.district ? "Please select a district first" : ""
                }
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  defaultValue="All Samplers"
                  placeholder="All Samplers"
                  className="filter-select"
                  style={{ width: "100%" }}
                  disabled={!filters?.district}
                  options={getSamplerFilters(samplers)}
                  value={filters?.sampler || "All Samplers"}
                  onChange={(value) => handleFilterChange("sampler", value)}
                />
              </Tooltip>
            </Col>
          </Row>
        </Card>
        <Card
          style={{
            borderRadius: 16,
            background: "#fff",
            boxShadow:
              "0 4px 24px 0 rgba(44, 39, 56, 0.08), 0 1.5px 4px 0 rgba(44, 39, 56, 0.08)",
            border: "none",
            padding: 0,
          }}
          bodyStyle={{ paddingBottom: 0 }}
        >
          <Row justify="space-between" style={{ marginBottom: 16 }}>
            <Title level={5}>Sampling Kits Overview</Title>
            <Tag
              style={{
                fontWeight: 700,
                fontSize: 15,
                background: "#E5E8EC",
                borderRadius: 6,
                padding: "6px 12px",
                border: "none",
              }}
            >
              {kitListsCount} {`Kit${kitListsCount > 1 ? "s" : ""}`}
            </Tag>
          </Row>
          <Table
            dataSource={dataSource}
            columns={columns}
            pagination={{
              current: page,
              pageSize: pageLimit,
              total: kitListsCount,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
              showTotal: (total, range) => (
                <Text
                  style={{ fontWeight: 600 }}
                >{`${range[0]}-${range[1]} of ${total} items`}</Text>
              ),
            }}
            onChange={handlePaginationChange}
            bordered={false}
            style={{ padding: 0, marginBottom: 0 }}
            scroll={{ y: "calc(100vh - 520px)", x: "max-content" }}
            loading={isLoading}
          />
        </Card>
        <CreateKit
          isCreateKitModalVisible={isCreateKitModalVisible}
          setIsCreateKitModalVisible={setIsCreateKitModalVisible}
          setIsAssignSamplerModalVisible={setIsAssignSamplerModalVisible}
          fetchKitLists={fetchKitLists}
        />
        <AssignSampler
          isAssignSamplerModalVisible={isAssignSamplerModalVisible}
          setIsAssignSamplerModalVisible={setIsAssignSamplerModalVisible}
          kitData={kitDataAfterKitCreation}
          fetchKitLists={fetchKitLists}
        />
        <SuccessModal />
      </div>
    </Layout>
  );
};

export default AssetManagement;